import { ApiStatus } from '../../types';
import {
    GenericBladeInfo,
    GenericBladeInitParam,
    GenericBladeState,
    GenericBladeType,
} from './genericBladeTypes';

export const getGenericBladePreloadState = (
    defaultGenericBladeState: GenericBladeInitParam[],
): GenericBladeState => {
    const result = defaultGenericBladeState.reduce((acc, curr) => ({
        ...acc,
        [curr.id]: {
            ...curr,
            apiFetchStatus: ApiStatus.Idle,
            apiSubmitStatus: ApiStatus.Idle,
        } as GenericBladeInfo,
    }), {});

    //duplicated Id
    if (Object.keys(result).length === defaultGenericBladeState.length) {
        return result;
    }
    throw new Error(
        'Please make sure you pass unique id for you generic blades.',
    );
};

export const genericInputBladePreloadTemplate = {
    id: 'generic-input-two-forms',
    genericBladeType: GenericBladeType.input,
    urlFetchEndPoint: '/genericInput',
    urlSubmitEndPoint: '/genericPost',
    content: {
        heading: '{heading}',
        description: '{description}',
        form1: {
            heading: '{heading 1}',
            description: '{description 1}',
            fieldType: 'currency',
            label: '{label 1}',
            showFormDivider: true,
        },
        form2: {
            heading: '{heading 2}',
            description: '{description 2}',
            label: '{label 2}',
            fieldType: 'currency',
        },
        confirmButtonText: '{confirmButtonText}',
        cancelButtonText: '{cancelButtonText}',
        validations: {
            hardValidations: {
                form1: {
                    defaultErrorMessage: '{form1 error}',
                    minValue: '0.01',
                    minValueMessage: '{Entered value should be +ve}',
                    baseValue: '0',
                    baseValueMessage: '{Entered amount should be greater than zero}',
                },
                form2: {
                    minValue: '0',
                    negativeValueMessage: '{Amount should be positive}',
                    minValueMessage: '{Minimum value is 0}',
                    defaultErrorMessage: '{form2 error}',
                },
            },
            softValidations: {
                form1: {
                    minValue: '700',
                    minValueMessage: '{Value is low}',
                    maxValue: '10000',
                    maxValueMessage: '{Maximum value is 10000}',
                },
                form2: {
                    minValue: '1000',
                    minValueMessage: '{Minimum value is 1000}',
                    maxValue: '10000',
                    maxValueMessage: '{Maximum value is 10000}',
                    baseValue: '0',
                    baseValueMessage: '{0 Amount?}',
                },
            },
        },
    },
};

export const BladeSelectiveInputPreloadTemplate: GenericBladeInitParam = {
    id: 'generic-selective-input',
    genericBladeType: GenericBladeType.selectiveInput,
    urlFetchEndPoint: '/genericSelectiveInput',
    urlSubmitEndPoint: '/genericPost',
    content: {
        heading: '{heading placeholder}',
        description: '{description placeholder}',
        dropdownOptions: [
            {
                key: 'Label 1',
                value: 'value1:payment',
            },
            {
                key: 'Label 2',
                value: 'value2:payment',
            },
            {
                key: 'Label 3',
                value: 'value3:payment',
            },
            {
                key: 'Label 4',
                value: 'value4:payment',
            },
            {
                key: 'Label 5',
                value: 'value5:payment',
            },
        ],
        confirmButtonText: '{confirmButtonText}',
        cancelButtonText: '{cancelButtonText}',
        validations: {
            hardValidations: {
                dropdown: {
                    defaultErrorMessage: '{SelectValue}',
                },
                payment: {
                    minValue: '0',
                    minValueMessage: '{Amount should be positive}',
                    defaultErrorMessage: '{Enter amount for Value 2}',
                },
            },
            softValidations: {
                payment: {
                    minValue: '1501',
                    minValueMessage: '{Value is low}',
                    baseValue: '0',
                    baseValueMessage: '',
                },
            },
        },
        dropdownLabel: '{dropdownLabel placeholder}',
        payment: {
            label: 'value2 label',
            hideInput: ['value1'],
        },
    },
};

const {
    id: inputBladeId,
    content: {
        form1: {
            heading: inputBladeHeading1,
            description: inputBladeDescription1,
            showFormDivider,
            ...restInputForm1
        },
        form2: {
            heading: heading2, description: description2, ...restInputForm2
        },
        ...restInputAem
    },
    ...inputBladeRestState
} = genericInputBladePreloadTemplate;

export const inputBladeWithTwoForms = genericInputBladePreloadTemplate as GenericBladeInitParam;

export const inputBladeWithOneForm = {
    id: 'generic-input-one-form',
    content: {
        form1: {
            heading: inputBladeHeading1,
            description: inputBladeDescription1,
            ...restInputForm1,
        },
        ...restInputAem,
    },
    ...inputBladeRestState,
} as GenericBladeInitParam;

export const inputBladeWithTwoInputs = {
    id: 'generic-input-two-inputs',
    content: {
        form1: { showFormDivider: true, ...restInputForm1 },
        form2: { ...restInputForm2 },
        ...restInputAem,
    },
    ...inputBladeRestState,
} as GenericBladeInitParam;

export const inputBladeWithOneInput = {
    id: 'generic-input-one-input',
    content: {
        form1: { ...restInputForm1 },
        ...restInputAem,
    },
    ...inputBladeRestState,
} as GenericBladeInitParam;

const genericDropdownBladePreloadTemplate = {
    id: 'generic-dropdown-two-forms',
    genericBladeType: GenericBladeType.dropdown,
    urlFetchEndPoint: '/genericDropdown',
    urlSubmitEndPoint: '/genericPost',
    content: {
        heading: '{heading}',
        description: '{description}',
        form1: {
            heading: '{heading 1}',
            description: '{description 1}',
            label: '{label 1}',
            showFormDivider: true,
            dropdownOptions: [
                {
                    value: '{value 1 placeholder 1}',
                    key: '{label 1 placeholder 1}',
                },
                {
                    value: '{value 2 placeholder 1}',
                    key: '{label 2 placeholder 1}',
                },
            ],
        },
        form2: {
            heading: '{heading 2}',
            description: '{description 2}',
            label: '{label 2}',
            dropdownOptions: [
                {
                    value: '{value 1 placeholder 2}',
                    key: '{label 1 placeholder 2}',
                },
                {
                    value: '{value 2 placeholder 2}',
                    key: '{label 2 placeholder 2}',
                },
            ],
        },
        confirmButtonText: '{confirmButtonText}',
        cancelButtonText: '{cancelButtonText}',
        validations: {
            hardValidations: {
                form1: {
                    defaultErrorMessage: '{form1 error}',
                },
                form2: {
                    defaultErrorMessage: '{form2 error}',
                },
            },
        },
    },
};

const {
    id: dropdownId,
    content: {
        form1: {
            heading: heading1Dropdown,
            description: description1Dropdown,
            showFormDivider: showFormDividerDropdown,
            ...restForm1Dropdown
        },
        form2: { heading: heading2Dropdown, description: description2Dropdown, ...restForm2Dropdown },
        validations: {
            hardValidations: {
                form1: form1HardValidationDropdown,
                form2: form2HardValidationDropdown,
            },
        },
        ...restAemDropdown
    },
    ...restStateDropdown
} = genericDropdownBladePreloadTemplate;

export const dropdownBladeWithTwoForms = genericDropdownBladePreloadTemplate as GenericBladeInitParam;

export const dropdownBladeWithOneForm = {
    id: 'generic-dropdown-one-form',
    content: {
        form1: {
            heading: heading1Dropdown,
            description: description1Dropdown,
            ...restForm1Dropdown,
        },
        validations: {
            hardValidations: {
                form1: form1HardValidationDropdown,
            },
        },
        ...restAemDropdown,
    },
    ...restStateDropdown,
} as GenericBladeInitParam;

export const dropdownBladeWithTwoDropdowns = {
    id: 'generic-dropdown-two-dropdowns',
    content: {
        form1: { showFormDivider: true, ...restForm1Dropdown },
        form2: { ...restForm2Dropdown },
        validations: {
            hardValidations: {
                form1: form1HardValidationDropdown,
                form2: form2HardValidationDropdown,
            },
        },
        ...restAemDropdown,
    },
    ...restStateDropdown,
} as GenericBladeInitParam;

export const dropdownBladeWithOneDropdown = {
    id: 'generic-dropdown-one-dropdown',
    content: {
        form1: { ...restForm1Dropdown },
        validations: {
            hardValidations: {
                form1: form1HardValidationDropdown,
            },
        },
        ...restAemDropdown,
    },
    ...restStateDropdown,
} as GenericBladeInitParam;

export const genericRadioBladePreloadTemplate = {
    id: 'generic-radio-hidden-input',
    genericBladeType: GenericBladeType.radio,
    urlFetchEndPoint: '/genericRadio',
    urlSubmitEndPoint: '/genericPost',
    content: {
        heading: '{heading placeholder}',
        description:
            '{description placeholder}',
        confirmButtonText: '{confirmButtonText placeholder}',
        form1: {
            label: '',
            options: [
                {
                    value: '{Value 0}',
                    label: '{Option 0}',
                },
                {
                    value: '{Value 1}',
                    label: '{Option 1}',
                },
                {
                    value: '{Value 2}',
                    label: '{Option 2}',
                },
                {
                    value: '{Value Other}',
                    label: '{Other}',
                },
            ],
        },
        validations: {
            hardValidations: {
                form1: {
                    maxLength: '2',
                    minValue: '0',
                    maxValue: '99',
                    errorRadioEmpty: '{Hard Validation - No option selected}',
                    errorHiddenInputNonDigit: '{Hard Validation - Non-digit}',
                    errorHiddenInputMaxLength: '{Hard Validation - Max length exceeded}',
                    errorHiddenInputEmpty: '{Hard Validation - Empty input}',
                },
            },
            softValidations: {
                form1: {
                    minValue: '0',
                    maxValue: '19',
                    infoHiddenInput: '{Soft validation - message}',
                },
            },
        },
        hiddenInputs: {
            form1: {
                label: '{Hidden Input label}',
                values: ['{Value 0}', '{Value 1}', '{Value 2}'],
            },
        },
    },
};

export const radioBladeWithHiddenInput =
    genericRadioBladePreloadTemplate as GenericBladeInitParam;

const {
    id,
    urlFetchEndPoint,
    ...restState
} = BladeSelectiveInputPreloadTemplate;

const selectiveInputDefaultState = {
    id: 'generic-selective-input-default',
    ...restState,
};

const selectiveInputWithApiData = {
    id: 'generic-selective-input-api-data',
    urlFetchEndPoint,
    ...restState,
};

export const genericConfirmBladePreloadData: GenericBladeInitParam[] = [
    {
        id: 'generic-confirm',
        genericBladeType: GenericBladeType.confirm,
        urlSubmitEndPoint: '/genericPost',
        content: {
            heading: '{heading}',
            description: '{description}',
            confirmButtonText: '{confirmButtonText}',
            cancelButtonText: '{cancelButtonText}',
        },
    },
    {
        id: 'generic-confirm-decision',
        genericBladeType: GenericBladeType.confirm,
        urlFetchEndPoint: '/genericConfirmDecision',
        urlSubmitEndPoint: '/genericPost',
        urlCancelEndPoint: '/genericCancel',
        content: {
            heading: '{heading}',
            description: '{description}',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        },
    },
    {
        id: 'generic-confirm-steps',
        genericBladeType: GenericBladeType.confirm,
        urlSubmitEndPoint: '/genericPost',
        content: {
            heading: '{heading}',
            description: '{description}',
            callouts: ['Application Number: '],
            calloutParams: ['12345678'],
            stepsHeading: '{stepsHeading}',
            steps: [
                '{Confirm Step 1}',
                '{Confirm Step 2}',
                '{Confirm Step 3}',
            ],
            stepsDescription: [
                '{Description Step 1}',
                '{Description Step 2}',
                '{Description Step 3}',
            ],
            stepsFooter: '{stepsFooter}',
            confirmButtonText: '{Yes}',
        },
    },
    {
        id: 'generic-confirm-steps-with-image',
        genericBladeType: GenericBladeType.confirm,
        urlSubmitEndPoint: '/genericPost',
        content: {
            img: 'https://www.commbank.com.au/content/dam/caas/NetBank/EverydayOrigination/NetBankSaverIllustration.svg',
            heading: '{heading}',
            description: '{description}',
            stepsHeading: '{stepsHeading}',
            steps: [
                '{Confirm Step 1}',
                '{Confirm Step 2}',
                '{Confirm Step 3}',
            ],
            stepsDescription: [
                '{Description Step 1}',
                '{Description Step 2}',
                '{Description Step 3}',
            ],
            confirmButtonText: '{Yes}',
        },
    },
];

export const BladeTimeDurationBladePreloadData: GenericBladeInitParam[] = [
    {
        id: 'generic-time-duration',
        genericBladeType: GenericBladeType.timeDuration,
        urlFetchEndPoint: '/genericTimeDuration',
        urlSubmitEndPoint: '/genericPost',
        content: {
            heading: '{heading}',
            description: '{description}',
            years: {
                label: '{Years}',
                defaultValue: '{Select}',
                dropdownOptions: [
                    {
                        key: '0',
                        value: '0',
                    },
                    {
                        key: '1',
                        value: '1',
                    },
                    {
                        key: '2',
                        value: '2',
                    },
                    {
                        key: '3',
                        value: '3',
                    },
                    {
                        key: '4',
                        value: '4',
                    },
                    {
                        key: '5',
                        value: '5',
                    },
                    {
                        key: '6',
                        value: '6',
                    },
                    {
                        key: '7',
                        value: '7',
                    },
                    {
                        key: '8',
                        value: '8',
                    },
                    {
                        key: '9',
                        value: '9',
                    },
                    {
                        key: '10',
                        value: '10',
                    },
                    {
                        key: '11',
                        value: '11',
                    },
                    {
                        key: '12',
                        value: '12',
                    },
                    {
                        key: '13',
                        value: '13',
                    },
                    {
                        key: '14',
                        value: '14',
                    },
                    {
                        key: '15',
                        value: '15',
                    },
                ],
            },
            months: {
                label: '{Months}',
                defaultValue: '{Select}',
                dropdownOptions: [
                    {
                        key: '0',
                        value: '0',
                    },
                    {
                        key: '1',
                        value: '1',
                    },
                    {
                        key: '2',
                        value: '2',
                    },
                    {
                        key: '3',
                        value: '3',
                    },
                    {
                        key: '4',
                        value: '4',
                    },
                    {
                        key: '5',
                        value: '5',
                    },
                    {
                        key: '6',
                        value: '6',
                    },
                    {
                        key: '7',
                        value: '7',
                    },
                    {
                        key: '8',
                        value: '8',
                    },
                    {
                        key: '9',
                        value: '9',
                    },
                    {
                        key: '10',
                        value: '10',
                    },
                    {
                        key: '11',
                        value: '11',
                    },
                    {
                        key: '12',
                        value: '12',
                    },
                ],
            },
            validations: {
                hardValidations: {
                    years: {
                        defaultErrorMessage: '{select years}',
                    },
                    months: {
                        defaultErrorMessage: '{select months}',
                    },
                },
            },
            confirmButtonText: '{Next}',
        },
    },
];

export const BladeDropdownPreloadData: GenericBladeInitParam[] = [
    dropdownBladeWithOneForm, dropdownBladeWithTwoForms, dropdownBladeWithOneDropdown, dropdownBladeWithTwoDropdowns ];

export const BladeRadioPreloadData: GenericBladeInitParam[] = [
    radioBladeWithHiddenInput];

export const BladeInputPreloadData: GenericBladeInitParam[] = [
    inputBladeWithTwoForms, inputBladeWithOneForm, inputBladeWithTwoInputs, inputBladeWithOneInput ];

export const BladeSelectiveInputPreloadData: GenericBladeInitParam[] = [selectiveInputDefaultState, selectiveInputWithApiData];
